import React, { useEffect, useState } from 'react';
import { Router, Switch, Route } from 'react-router-dom';
import { Loader, ToastCluster } from '@skf/bt-wind-rail-commonlibrary';
import style from './App.module.scss';
import { Routes } from '../../routes/routes';
import { HeaderLayout } from '../layout/header/HeaderLayout';
import { FooterLayout } from '../layout/footer/FooterLayout';
import { DashboardLayout } from '../layout/dashboard/DashboardLayout';
import { History } from 'history';
import { useUserStatus } from '../../services/authentication/useUserStatus';
import { useLoadingScreen } from '../../hooks/useLoadingScreen';
import { usePathname } from '../../hooks/usePathname';
import { useToast } from '../../hooks/useToast';
import { IUsersState } from '../../store/users/store';
import { useDispatch, useSelector } from 'react-redux';
import { StoreState } from '../../store/store';
import {
  clearUsersError,
  clearWaitingUsers,
  fetchPortals,
  fetchUsersAndWaitingForStatus,
} from '../../store/users/actions';
import { RoleAssignmentStatus } from '../../store/users/types';
import { useTranslation } from 'react-i18next';
import { fetchUserSettings } from '../../store/userSettings/actions';
import { logSection } from '../../services/sessionService';
import {
  getDefaultLocation,
  signOut,
} from '../../services/location/locationService';
import { fetchSectionsAction } from '../../store/sections/actions';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
} from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { INotificationsState } from '../../store/notifications/store';

interface IAppProps {
  history: History;
}

const App: React.FunctionComponent<IAppProps> = ({ history }) => {
  const { instance, accounts, inProgress } = useMsal();

  //#region MSAL

  const [callApi, setCallApi] = useState(true);

  //Error logging for SSO - Based on darwin-web-main -> authConsumer/index.tsx:79
  instance.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as AuthenticationResult;
      const account = payload.account;
      instance.setActiveAccount(account);
    } else if (event.error?.message.includes('AADB2C90077')) {
      console.error('SSO ERROR - UnknownSSOError', JSON.stringify(event.error));
    } else if (
      (event.eventType === EventType.LOGIN_FAILURE &&
        event.error?.constructor.name !== 'BrowserAuthError') ||
      (event.eventType === EventType.SSO_SILENT_FAILURE &&
        event.error?.message.includes('monitor_window_timeout'))
    ) {
      console.error('SSO ERROR - NotFound', JSON.stringify(event.error));
    } else if (
      [
        EventType.ACQUIRE_TOKEN_FAILURE,
        EventType.LOGIN_FAILURE,
        EventType.LOGOUT_FAILURE,
        EventType.ACQUIRE_TOKEN_FAILURE,
        EventType.ACQUIRE_TOKEN_BY_CODE_FAILURE,
      ].includes(event.eventType)
    ) {
      console.error('SSO ERROR - UnknownSSOError', JSON.stringify(event.error));
    }
  });

  const activeAccount = instance.getActiveAccount() || accounts[0];
  //#endregion

  // const { user, signOut, checkSession, setCheckSession } = useUserStatus();
  const { isLoading } = useLoadingScreen();
  const [location, setLocation] = useState(
    getDefaultLocation(window.location.pathname)
  );
  const { setPathname, pathname, setHistory } = usePathname();
  const { lastToast, addToast } = useToast();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { waitingForStatusUsers, users }: IUsersState = useSelector<
    StoreState,
    IUsersState
  >((state) => state.users);
  const { notification }: INotificationsState = useSelector<
    StoreState,
    INotificationsState
  >((state) => state.notifications);

  useEffect(() => {
    if (notification?.message) {
      addToast({
        message: notification?.message,
        status: 'warning',
      });
      dispatch(clearUsersError());
    }
  }, [notification?.message]);

  useEffect(() => {
    const currentRoute = Routes.filter(
      (route) => route.webSection
    ).find((route) => pathname.startsWith(route.path));
    if (!currentRoute) return;
  }, [pathname]);

  useEffect(() => {
    const pathNameClean = pathname.substring(1).replace('/', '||');
    if (pathNameClean) logSection('Admin Portal||' + pathNameClean);
  }, [pathname]);

  useEffect(() => {
    setHistory(history);
  }, [history]);

  useEffect(() => {
    history.listen((loc) => {
      setPathname(loc.pathname);
      setLocation(loc.pathname);
    });
  }, []);

  /**
   * Check if are waiting users to toastify
   */
  useEffect(() => {
    setTimeout(() => {
      if (!waitingForStatusUsers || waitingForStatusUsers?.length === 0) return;

      // Users waiting for status
      const usersWaiting =
        users?.filter(
          (_user) =>
            waitingForStatusUsers.indexOf(_user.userName?.toLowerCase()) > -1
        ) || [];
      // Users with "done" status.
      const usersToBeCleared = usersWaiting.filter(
        (_user) =>
          _user.roleAssignmentStatus === RoleAssignmentStatus.ERROR ||
          _user.roleAssignmentStatus === RoleAssignmentStatus.DONE
      );
      // Users without "done" status
      const usersToCheckAgain = usersWaiting.filter(
        (user2) =>
          !usersToBeCleared.some(
            (_user) =>
              user2.userName?.toLowerCase() === _user.userName?.toLowerCase()
          )
      );

      // Check again users without "done" status
      if (usersToCheckAgain.length > 0)
        dispatch(fetchUsersAndWaitingForStatus(usersToCheckAgain));
      // Clear users with "done" status
      if (usersToBeCleared.length > 0)
        dispatch(
          clearWaitingUsers(
            usersToBeCleared.map((_user) => _user.userName?.toLowerCase())
          )
        );

      // Add toasts for the "done" users
      usersToBeCleared.forEach((_user) =>
        addToast({
          message: (_user.roleAssignmentStatus === RoleAssignmentStatus.ERROR
            ? t('Roles_assignment_for_user_not_completed')
            : t('Roles_assignment_for_user_completed')
          ).replace('{username}', _user.userName),
          status:
            _user.roleAssignmentStatus === RoleAssignmentStatus.ERROR
              ? 'warning'
              : 'ok',
        })
      );
    }, 10 * 1000);
  }, [waitingForStatusUsers, users]);

  useEffect(() => {
    if (inProgress === InteractionStatus.None && activeAccount && callApi) {
      dispatch(fetchUserSettings());
      dispatch(fetchSectionsAction());
      dispatch(fetchPortals());
      setCallApi(false);
    }
  }, [inProgress, activeAccount, callApi, dispatch, instance]);

  return (
    <>
      <Loader fullscreen={true} show={isLoading} />
      <ToastCluster
        className={style.toastCluster}
        position="topRight"
        toastMessage={lastToast}
      />
      <div className={isLoading ? style.blurHome : ''}>
        <Router history={history}>
          <HeaderLayout
            signOut={() => signOut(true)}
            userEmail={activeAccount?.idTokenClaims?.email as string}
            pathname={location}
          />
          <DashboardLayout>
            <Switch>
              {Routes.map((route, index) => (
                <Route
                  key={index}
                  path={route.path}
                  exact
                  component={route.component}
                />
              ))}
            </Switch>
          </DashboardLayout>
          <FooterLayout />
        </Router>
      </div>
    </>
  );
};

export default App;
